import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import gsap from 'gsap';
import { Avatar } from 'src/game/components/shared/Avatar';
import sounds from 'src/lib/sounds';
import { streakBattleRoyaleTheme } from '../streakbattleroyale.theme';
import { PI_2 } from 'src/game/minigame/match2-timestep/odie/2d/display/const';
import {
  randomFloat,
  randomInt,
  XY,
} from 'src/game/minigame/match2-timestep/odie';
import { user } from 'src/StateObserver';
import StreakCloudPlatform from './StreakCloudPlatform';
import { getPlayerProfileAvatarFrame } from 'src/replicant/profile/profile.getters';

type StreakPlayerOpts = {
  playerId: string;
  playerPhoto?: string;
} & ViewOpts;

type TweenOpts = {
  platform?: StreakCloudPlatform;
  x: number;
  y: number;
  scale: number;
  delay?: number;
  instant?: boolean;
};

const scale = 1.25;
const width = 92 * scale;
const height = 96 * scale;
const tweenDuration = 0.5;
const deathDuration = 0.85;
const blinks = 4;

export default class StreakPlayerV2 extends View {
  private transformView: View;
  private avatar: Avatar;
  private deathIcon: ImageScaleView;
  private deathFrame: ImageScaleView;
  private _playerId: string;
  private originalScale = 1;
  private platform?: StreakCloudPlatform;

  public get playerId() {
    return this._playerId;
  }

  constructor(opts: StreakPlayerOpts) {
    super(opts);
    this._playerId = opts.playerId;

    this.transformView = new View({ superview: this });

    const me = user().id === opts.playerId;

    this.avatar = new Avatar({
      superview: this.transformView,
      width,
      height,
      borderTint: me ? '#FFF503' : undefined,
      dropShadow: { offsetY: 6 },
      centerOnOrigin: true,
      frame: me ? getPlayerProfileAvatarFrame(user()) : 'default',
    });

    this.avatar.updateImage(opts.playerPhoto ?? '');

    this.deathIcon = new ImageScaleView({
      superview: this.transformView,
      image: streakBattleRoyaleTheme().getAsset('icon_death'),
      width: height,
      height,
      centerOnOrigin: true,
      visible: false,
    });

    this.deathFrame = new ImageScaleView({
      superview: this.transformView,
      image: streakBattleRoyaleTheme().getAsset('frame_death'),
      width: 104 * 1.58,
      height: 107 * 1.58,
      x: -1,
      y: -4,
      centerOnOrigin: true,
      visible: false,
    });
  }

  public async moveTo({
    platform,
    x,
    y,
    scale,
    delay,
    instant = false,
  }: TweenOpts) {
    this.originalScale = scale;
    this.platform = instant ? platform : undefined;

    if (instant) {
      this.updateOpts({
        x,
        y,
        scale,
      });

      return;
    }

    await gsap.to(this.style, {
      duration: 0.3,
      scale,
      x,
      y,
      ease: 'quad.inOut',
      delay,
    });

    this.platform = platform;
  }

  public async jumpTo({
    platform,
    x,
    y,
    scale,
    delay,
    instant = false,
  }: TweenOpts) {
    this.originalScale = scale;
    this.platform = instant ? platform : undefined;

    if (instant) {
      this.updateOpts({
        x,
        y,
        scale,
      });

      return;
    }

    await gsap
      .timeline({
        delay,
        onStart: () => {
          sounds.sfx.play('fujiquest-jump');
        },
      })
      .to(this.transformView.style, {
        duration: 0.2,
        scaleY: 0.85,
        y: 15,
        ease: 'quad.inOut',
      })
      .to(this.transformView.style, {
        duration: 0.2,
        scaleY: 1.15,
        y: 0,
        ease: 'quad.inOut',
      })
      .to(
        this.transformView.style,
        {
          duration: tweenDuration / 2,
          r: Math.sign(this.style.x - x) / 3,
          ease: 'quad.inOut',
        },
        '<',
      )
      .to(
        this.style,
        {
          duration: tweenDuration,
          x,
          scale,
          ease: 'quad.inOut',
        },
        '<',
      )
      .to(
        this.style,
        {
          duration: tweenDuration,
          y,
          ease: this.style.y < y ? 'back.in' : 'back.out(4)',
          onStart: () => {
            gsap
              .timeline()
              .to(
                this.transformView.style,
                {
                  duration: 0.5,
                  scaleY: 1,
                  ease: 'back.out(6)',
                },
                (tweenDuration * 2) / 3,
              )
              .to(
                this.transformView.style,
                {
                  duration: 1.2,
                  r: 0,
                  ease: 'elastic.out(1.5,0.45)',
                },
                '<',
              );
          },
        },
        '<',
      );

    this.platform = platform;
  }

  public async eliminate(origin: XY, instant = false) {
    this.platform = undefined;

    const scale = this.originalScale * randomFloat(1.5, 3);

    this.updateOpts({
      opacity: 1,
      zIndex: Math.ceil(scale * 10),
    });

    this.transformView.updateOpts({
      scale: 1,
      r: 0,
    });

    this.deathIcon.updateOpts({
      opacity: 0,
      visible: true,
    });

    const ox = this.style.x - origin.x;
    const oy = this.style.y - origin.y;
    const mx = ox / 75;
    const my = oy / 75;

    this.deathFrame.updateOpts({
      opacity: 0,
      visible: true,
    });

    const duration = instant ? 0 : deathDuration;

    await gsap
      .timeline()
      .to(this.transformView.style, {
        duration: deathDuration,
        r: mx ? PI_2 * mx * 2 : Math.random() * PI_2,
      })
      .to(
        this.deathIcon.style,
        {
          duration: instant ? 0 : (deathDuration * 0.75) / blinks,
          opacity: 1,
          repeat: blinks,
          yoyo: true,
          ease: 'steps(1)',
        },
        '<',
      )
      .to(
        this.deathFrame.style,
        {
          duration: instant ? 0 : deathDuration * 0.65,
          opacity: 1,
          ease: 'power3.in',
        },
        '<',
      )
      .to(
        this.style,
        {
          duration,
          x: this.style.x + 150 * mx + randomInt(-100, 100),
          ease: 'quad.out',
        },
        '<',
      )
      .to(
        this.style,
        {
          duration,
          scale,
          ease: 'quad.in',
        },
        '<',
      )
      .to(
        this.style,
        {
          duration,
          y: this.style.y + 500 + 150 * my + randomInt(-150, 150),
          ease: 'back.in(2)',
          onInterrupt: () => {
            this.removeFromSuperview();
          },
          onComplete: () => {
            this.removeFromSuperview();
          },
        },
        '<',
      )
      .to(
        this.style,
        {
          duration: instant ? 0 : deathDuration / 2,
          opacity: 0,
          ease: 'quad.in',
        },
        `<+=${deathDuration / 2}`,
      );

    this.deathIcon.style.visible = false;
    this.deathFrame.style.visible = false;
  }

  public tick(dt: number) {
    if (this.platform) {
      this.transformView.style.y =
        -Math.sin(this.platform.idleRatio * PI_2 + 0.6) * 6;
    }
  }
}
